import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { city: "", active: false, delivery_fee: 40, free_delivery_above: 999, min_order: 199, eta: "45-60 mins", pincodes: [] };

export default function AdminDeliveryAreas() {
  const [areas, setAreas] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/delivery-areas").then((r) => setAreas(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ ...EMPTY, ...a, pincodes: a.pincodes || [] }); setEditId(a.id); setOpen(true); };

  const toggle = async (a) => {
    await api.put(`/admin/delivery-areas/${a.id}`, { ...a, active: !a.active });
    toast.success(`${a.city} ${!a.active ? "activated" : "deactivated"}`);
    load();
  };

  const save = async () => {
    const payload = {
      city: form.city, active: form.active,
      delivery_fee: Number(form.delivery_fee), free_delivery_above: Number(form.free_delivery_above),
      min_order: Number(form.min_order), eta: form.eta,
      pincodes: typeof form.pincodes === "string" ? form.pincodes.split(",").map((p) => p.trim()).filter(Boolean) : form.pincodes,
    };
    try {
      if (editId) await api.put(`/admin/delivery-areas/${editId}`, payload);
      else await api.post("/admin/delivery-areas", payload);
      toast.success("Delivery area saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.city}?`)) return;
    await api.delete(`/admin/delivery-areas/${a.id}`); toast.success("Deleted"); load();
  };

  const F = ({ label, k, type = "number" }) => (
    <div><label className="text-xs font-medium text-muted-foreground">{label}</label>
      <input type={type} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`area-${k}`} className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" /></div>
  );

  return (
    <div data-testid="admin-delivery-areas">
      <div className="flex justify-between items-center mb-6">
        <div><h1 className="font-serif text-3xl text-navy flex items-center gap-2"><MapPin className="text-gold" /> Delivery Areas</h1><p className="text-sm text-muted-foreground">Control where you deliver, charges, minimum order & ETA. Serviceability is enforced at checkout.</p></div>
        <button onClick={openNew} data-testid="add-area-btn" className="flex items-center gap-2 bg-navy text-cream text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-navy-soft"><Plus size={15} /> Add Area</button>
      </div>

      <div className="bg-white rounded-xl border border-black/5 overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-beige text-navy text-left"><tr><th className="p-3">City</th><th className="p-3">Status</th><th className="p-3">Delivery Fee</th><th className="p-3">Free Above</th><th className="p-3">Min Order</th><th className="p-3">ETA</th><th className="p-3">Pincodes</th><th className="p-3"></th></tr></thead>
          <tbody>
            {areas.map((a) => (
              <tr key={a.id} className="border-t border-black/5" data-testid={`area-row-${a.city.toLowerCase().replace(/\s+/g, "-")}`}>
                <td className="p-3 font-medium text-navy">{a.city}</td>
                <td className="p-3"><div className="flex items-center gap-2"><Switch checked={a.active} onCheckedChange={() => toggle(a)} data-testid={`area-toggle-${a.city.toLowerCase().replace(/\s+/g, "-")}`} /><span className={`text-xs ${a.active ? "text-green-700" : "text-amber-700"}`}>{a.active ? "ACTIVE" : "INACTIVE"}</span></div></td>
                <td className="p-3">₹{a.delivery_fee}</td>
                <td className="p-3">₹{a.free_delivery_above}</td>
                <td className="p-3">₹{a.min_order}</td>
                <td className="p-3 text-muted-foreground">{a.eta}</td>
                <td className="p-3 text-muted-foreground text-xs" data-testid={`area-pincodes-count-${a.city.toLowerCase().replace(/\s+/g, "-")}`}>{(a.pincodes || []).length ? `${a.pincodes.length} listed` : "—"}</td>
                <td className="p-3 flex gap-2"><button onClick={() => openEdit(a)} data-testid={`edit-area-${a.city.toLowerCase().replace(/\s+/g, "-")}`} className="text-navy hover:text-gold"><Pencil size={15} /></button><button onClick={() => remove(a)} data-testid={`delete-area-${a.city.toLowerCase().replace(/\s+/g, "-")}`} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "Add"} Delivery Area</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="City / Area Name" k="city" type="text" />
            <div className="grid grid-cols-2 gap-3">
              <F label="Delivery Fee (₹)" k="delivery_fee" />
              <F label="Free Delivery Above (₹)" k="free_delivery_above" />
              <F label="Minimum Order (₹)" k="min_order" />
              <F label="Estimated Delivery Time" k="eta" type="text" />
            </div>
            <div><label className="text-xs font-medium text-muted-foreground">Pincodes / Zones (comma separated)</label>
              <input value={Array.isArray(form.pincodes) ? form.pincodes.join(", ") : form.pincodes} onChange={(e) => setForm({ ...form, pincodes: e.target.value })} data-testid="area-pincodes" className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="area-active" /> Serviceable (active)</label>
            <button onClick={save} data-testid="save-area-btn" className="w-full bg-navy text-cream font-semibold py-2.5 rounded-full hover:bg-navy-soft">{editId ? "Update" : "Create"} Area</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
